import React, { useState, useEffect } from 'react';
import { 
  IconMusic, 
  IconDeviceFloppy, 
  IconAlertCircle, 
  IconCurrencyEuro
} from '@tabler/icons-react';
import { 
  StaffType, 
  MusicGenre, 
  STAFF_TYPE_LABELS, 
  MUSIC_GENRE_LABELS,
  StaffProfile
} from '../types/staff';
import { staffService } from '../services/api';
import styles from './StaffProfileForm.module.css';

interface StaffProfileFormProps {
  existingProfile?: StaffProfile;
  onSubmitSuccess?: () => void;
}

// Types de staff liés à la musique qui nécessitent des genres musicaux
const MUSIC_RELATED_STAFF_TYPES = [
  StaffType.DJ,
  StaffType.MUSICIAN,
  StaffType.SOUND_ENGINEER
];

const StaffProfileForm: React.FC<StaffProfileFormProps> = ({ 
  existingProfile,
  onSubmitSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const convertGenresToArray = (genres: MusicGenre[] | string | undefined): MusicGenre[] => {
    if (!genres) return [];
    if (typeof genres === 'string') {
      return genres.split(',').filter(g => g.trim() !== '')
        .map(g => g.toUpperCase() as MusicGenre);
    }
    return genres;
  };

  const [staffType, setStaffType] = useState<StaffType>(
    existingProfile?.staffType || StaffType.DJ
  );
  const [selectedGenres, setSelectedGenres] = useState<MusicGenre[]>(
    convertGenresToArray(existingProfile?.genres)
  );
  const [experience, setExperience] = useState(
    existingProfile?.experience || ''
  );
  const [equipmentDescription, setEquipmentDescription] = useState(
    existingProfile?.equipmentDescription || ''
  );
  const [portfolio, setPortfolio] = useState(
    existingProfile?.portfolio || ''
  );
  const [soundcloudUrl, setSoundcloudUrl] = useState(
    existingProfile?.soundcloudUrl || ''
  );
  const [spotifyUrl, setSpotifyUrl] = useState(
    existingProfile?.spotifyUrl || ''
  );
  const [bandcampUrl, setBandcampUrl] = useState(
    existingProfile?.bandcampUrl || ''
  );
  const [mixcloudUrl, setMixcloudUrl] = useState(
    existingProfile?.mixcloudUrl || ''
  );
  const [instagramUrl, setInstagramUrl] = useState(
    existingProfile?.instagramUrl || ''
  );
  const [availability, setAvailability] = useState(
    existingProfile?.availability || ''
  );
  const [hourlyRate, setHourlyRate] = useState<string>(
    existingProfile?.hourlyRate?.toString() || ''
  );
  const [isActive, setIsActive] = useState(
    existingProfile?.isActive ?? true
  );

  // Vérifier si le type de staff est lié à la musique
  const isMusicRelatedStaff = MUSIC_RELATED_STAFF_TYPES.includes(staffType);

  // Réinitialiser les genres musicaux si on change vers un type non-musical
  useEffect(() => {
    if (!isMusicRelatedStaff) {
      setSelectedGenres([]);
    }
  }, [staffType, isMusicRelatedStaff]);

  const handleGenreToggle = (genre: MusicGenre) => {
    if (selectedGenres.includes(genre)) {
      setSelectedGenres(selectedGenres.filter(g => g !== genre));
    } else {
      setSelectedGenres([...selectedGenres, genre]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Valider les genres musicaux uniquement si c'est un type de staff musical
    if (isMusicRelatedStaff && selectedGenres.length === 0) {
      setError("Veuillez sélectionner au moins un genre musical");
      setLoading(false);
      return;
    }

    if (!experience) {
      setError("Veuillez décrire votre expérience");
      setLoading(false);
      return;
    }

    try {
      // Convertir le staffType en minuscules pour correspondre au format attendu par le backend
      // Limiter aux valeurs acceptées par le backend (enum dans le DTO)
      let staffTypeFormatted = staffType.toLowerCase();
      if (!['dj', 'bartender', 'security', 'photographer'].includes(staffTypeFormatted)) {
        // Si le type n'est pas dans la liste autorisée, on utilise 'dj' par défaut
        console.warn(`Type de staff ${staffTypeFormatted} non supporté, utilisation de 'dj' par défaut`);
        staffTypeFormatted = 'dj';
      }
      
      // Convertir le tableau de genres en chaîne de caractères séparée par des virgules
      const genresFormatted = selectedGenres.join(',');
      
      // Construire l'objet avec uniquement les champs supportés par le backend
      const profileData: any = {
        staffType: staffTypeFormatted,
        genres: genresFormatted || 'other', // Assurer qu'il y a au moins une valeur
        experience: experience || '',
        equipmentDescription: equipmentDescription || '',
        availability: availability || 'weekends', // Valeur par défaut si vide
      };
      
      // Ajouter les champs optionnels seulement s'ils sont présents et valides
      if (portfolio && portfolio.trim() !== '') {
        profileData.portfolio = portfolio;
      }
      
      if (soundcloudUrl && soundcloudUrl.trim() !== '') {
        profileData.soundcloudUrl = soundcloudUrl;
      }
      
      if (spotifyUrl && spotifyUrl.trim() !== '') {
        profileData.spotifyUrl = spotifyUrl;
      }
      
      if (bandcampUrl && bandcampUrl.trim() !== '') {
        profileData.bandcampUrl = bandcampUrl;
      }
      
      if (mixcloudUrl && mixcloudUrl.trim() !== '') {
        profileData.mixcloudUrl = mixcloudUrl;
      }
      
      if (instagramUrl && instagramUrl.trim() !== '') {
        profileData.instagramUrl = instagramUrl;
      }
      
      // Convertir hourlyRate en nombre si présent
      if (hourlyRate && hourlyRate.trim() !== '') {
        const rate = parseFloat(hourlyRate);
        if (!isNaN(rate) && rate >= 0) {
          profileData.hourlyRate = rate;
        }
      }

      console.log("📤 Données envoyées au serveur:", profileData);

      if (existingProfile) {
        await staffService.updateStaffProfile({
          ...profileData,
          id: existingProfile.id
        });
      } else {
        await staffService.createStaffProfile(profileData);
      }

      setSuccess(true);
      
      // Réinitialiser le formulaire si c'est une création
      if (!existingProfile) {
        setStaffType(StaffType.DJ);
        setSelectedGenres([]);
        setExperience('');
        setEquipmentDescription('');
        setPortfolio('');
        setSoundcloudUrl('');
        setSpotifyUrl('');
        setBandcampUrl('');
        setMixcloudUrl('');
        setInstagramUrl('');
        setAvailability('');
        setHourlyRate('');
        setIsActive(true);
      }
      
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err) {
      console.error("❌ Erreur lors de la soumission:", err);
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null) {
        // Essayer d'extraire le message d'erreur de la réponse API si possible
        const errorResponse = (err as any).response?.data;
        if (errorResponse?.message) {
          if (Array.isArray(errorResponse.message)) {
            setError(errorResponse.message[0]); // Prendre la première erreur de validation
          } else {
            setError(errorResponse.message);
          }
        } else {
          setError("Une erreur est survenue lors de l'enregistrement du profil");
        }
      } else {
        setError("Une erreur est survenue lors de l'enregistrement du profil");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <h2 className={styles.formTitle}>
        <IconMusic size={24} />
        {existingProfile ? 'Modifier mon profil de staff' : 'Créer mon profil de staff'}
      </h2>
      
      {error && (
        <div className={styles.errorMessage}>
          <IconAlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}
      
      {success && (
        <div className={styles.successMessage}>
          <IconDeviceFloppy size={20} />
          <span>Votre profil de staff a été {existingProfile ? 'modifié' : 'créé'} avec succès !</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="staffType" className={styles.label}>Type de staff</label>
          <select 
            id="staffType"
            className={styles.select}
            value={staffType}
            onChange={(e) => setStaffType(e.target.value as StaffType)}
            required
          >
            {Object.entries(STAFF_TYPE_LABELS).map(([type, label]) => (
              <option key={type} value={type}>{label}</option>
            ))}
          </select>
        </div>
        
        {/* Afficher les genres musicaux uniquement si le type de staff est lié à la musique */}
        {isMusicRelatedStaff && (
          <div className={styles.formGroup}>
            <label className={styles.label}>Genres musicaux</label>
            <div className={styles.genresGrid}>
              {Object.entries(MUSIC_GENRE_LABELS).map(([genre, label]) => (
                <div key={genre} className={styles.genreItem}>
                  <label className={styles.genreLabel}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={selectedGenres.includes(genre as MusicGenre)}
                      onChange={() => handleGenreToggle(genre as MusicGenre)}
                    />
                    <span>{label}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className={styles.formGroup}>
          <label htmlFor="experience" className={styles.label}>Expérience</label>
          <textarea
            id="experience"
            className={styles.textarea}
            value={experience}
            onChange={(e) => setExperience(e.target.value)}
            required
            placeholder={`Décrivez votre expérience en tant que ${STAFF_TYPE_LABELS[staffType]}...`}
            rows={4}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="equipmentDescription" className={styles.label}>
            Équipement (optionnel)
          </label>
          <textarea
            id="equipmentDescription"
            className={styles.textarea}
            value={equipmentDescription}
            onChange={(e) => setEquipmentDescription(e.target.value)}
            placeholder="Décrivez votre équipement..."
            rows={3}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="availability" className={styles.label}>Disponibilité</label>
          <textarea
            id="availability"
            className={styles.textarea}
            value={availability}
            onChange={(e) => setAvailability(e.target.value)}
            required
            placeholder="Ex: Disponible les weekends et en soirée..."
            rows={2}
          />
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="hourlyRate" className={styles.label}>
            Tarif horaire (€/h) (optionnel)
          </label>
          <div className={styles.inputWithIcon}>
            <input
              id="hourlyRate"
              type="number"
              min="0"
              step="0.01"
              className={styles.input}
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              placeholder="Ex: 25"
            />
            <IconCurrencyEuro size={20} className={styles.inputIcon} />
          </div>
        </div>
        
        <div className={styles.formGroup}>
          <label htmlFor="portfolio" className={styles.label}>
            Portfolio / Site web (optionnel)
          </label>
          <input
            id="portfolio"
            type="url"
            className={styles.input}
            value={portfolio}
            onChange={(e) => setPortfolio(e.target.value)}
            placeholder="https://..."
          />
        </div>
        
        {/* Afficher les liens musicaux uniquement si le type de staff est lié à la musique */}
        {isMusicRelatedStaff && (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="soundcloudUrl" className={styles.label}>
                SoundCloud (optionnel)
              </label>
              <input
                id="soundcloudUrl"
                type="url"
                className={styles.input}
                value={soundcloudUrl}
                onChange={(e) => setSoundcloudUrl(e.target.value)}
                placeholder="https://soundcloud.com/..."
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="spotifyUrl" className={styles.label}>
                Spotify (optionnel)
              </label>
              <input
                id="spotifyUrl"
                type="url"
                className={styles.input}
                value={spotifyUrl}
                onChange={(e) => setSpotifyUrl(e.target.value)}
                placeholder="https://open.spotify.com/..."
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="bandcampUrl" className={styles.label}>
                Bandcamp (optionnel)
              </label>
              <input
                id="bandcampUrl"
                type="url"
                className={styles.input}
                value={bandcampUrl}
                onChange={(e) => setBandcampUrl(e.target.value)}
                placeholder="https://bandcamp.com/..."
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="mixcloudUrl" className={styles.label}>
                Mixcloud (optionnel)
              </label>
              <input
                id="mixcloudUrl"
                type="url"
                className={styles.input}
                value={mixcloudUrl}
                onChange={(e) => setMixcloudUrl(e.target.value)}
                placeholder="https://www.mixcloud.com/..."
              />
            </div>
          </>
        )}
        
        <div className={styles.formGroup}>
          <label htmlFor="instagramUrl" className={styles.label}>
            Instagram (optionnel)
          </label>
          <input
            id="instagramUrl"
            type="url"
            className={styles.input}
            value={instagramUrl}
            onChange={(e) => setInstagramUrl(e.target.value)}
            placeholder="https://www.instagram.com/..."
          />
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>Je suis actuellement disponible pour des événements</span>
          </label>
        </div>
        
        <div className={styles.formActions}>
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? 'Enregistrement...' : existingProfile ? 'Mettre à jour' : 'Créer mon profil'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StaffProfileForm;
